import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Organisation,
  OrganisationType,
  OwnerType,
  AddressType,
  PhoneNumberType,
} from "@/api/SolarCloudApi";
import { Input } from "@/components/ui/input";
import { FormCard } from "../../../components/FormCard";
import ExistingOrganisations from "./ExistingOrganisations";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router";
import { ABNInput } from "@/components/ui/ABNInput";
import {
  organisationSchema,
  useCreateOrganisation,
} from "@/queries/organisation/useCreateOrganisation";
import { useUser } from "@/queries/user/useUser";
import { useNewOrder } from "@/queries/order/useNewOrder";
import { useOrderUuid } from "@/stores/orderIdStore";
import { useUpdateOrganisation } from "@/queries/organisation/useUpdateOrganisation";
import { PlusIcon, EditIcon } from "lucide-react";
import { AddressForm } from "@/components/AddressForm";
import { CustomPhoneInput } from "@/components/ui/CustomPhoneInput";
import {
  addressSchema,
  phoneNumbersSchema,
} from "@/queries/user/useUpdateUserProfile";
import {
  findDefaultOrganisationPhoneNumber,
  findDefaultOrganisationAddress,
} from "@/util/organisation";
import LoadingSection from "@/components/LoadingSection";
import { isValidABN } from "@/util/isValidABN";
import { useOrganisationTypes } from "@/queries/organisation/useOrganisationTypes";
import { usePaymentGatekeeperStore } from "./paymentGatekeeper";
import type { PaymentGatekeeperOptions } from "./paymentGatekeeper";
import { scrollToFirstInvalidField } from "./formValidation";
import { useCountries } from "@/hooks/Countries";

const optionalOrganisationPhoneNumberSchema = z.object({
  uuid: z.string().optional(),
  type: z.nativeEnum(PhoneNumberType).optional(),
  number: z.string().optional(),
});

const optionalOrganisationAddressSchema = z.object({
  country: z.string().optional(),
  line1: z.string().optional(),
  line2: z.string().optional(),
  line3: z.string().optional(),
  line4: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postcode: z.string().optional(),
  type: z.nativeEnum(AddressType).optional(),
  uuid: z.string().optional(),
});

// Dynamic schema based on organization type
const createDynamicOrganisationSchema = (
  selectedType: string | undefined,
  organizationTypes: any[]
) => {
  const orgType = organizationTypes.find((type) => type.code === selectedType);

  return z.object({
    type: z.nativeEnum(OrganisationType).or(z.literal("")),
    name: orgType?.flags.nameRequired
      ? z
        .string()
        .min(1, { message: "Company name is required" })
        .max(200, {
          message: "Company name must be no more than 200 characters",
        })
        .trim()
      : z.string().optional(),
    abn: orgType?.flags.abnRequired
      ? z
        .string()
        .regex(/^\d{11}$/, { message: "Please enter a valid 11-digit ABN." })
        .refine(isValidABN, {
          message:
            "Invalid ABN. Please check that you've entered it correctly.",
        })
      : z.string().optional(),
    phoneNumbers: z.array(phoneNumbersSchema).optional(),
    addresses: z.array(addressSchema).optional(),
    uuid: z.string().optional(),
    phoneNumber: optionalOrganisationPhoneNumberSchema,
    address: optionalOrganisationAddressSchema,
  });
};

export default function OrganisationForm({
  onSuccess,
}: {
  onSuccess: (uuid: string) => void | Promise<void>;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingNewOrganisation, setIsAddingNewOrganisation] = useState(false);
  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined
  );
  const navigate = useNavigate();

  const { data: user, isLoading: isLoadingUser } = useUser();
  const { data: organizationTypes = [], isLoading: isLoadingOrgTypes } =
    useOrganisationTypes();
  const orderUuid = useOrderUuid();
  const { data: order, isLoading: isLoadingOrder } = useNewOrder({
    uuid: orderUuid,
  });

  const [selectedOrganisation, setSelectedOrganisation] =
    useState<Organisation | null>(null);

  const defaultPhoneNumber =
    findDefaultOrganisationPhoneNumber(selectedOrganisation);
  const defaultAddress = findDefaultOrganisationAddress(selectedOrganisation);

  const { countryFromCode } = useCountries();
  let defaultCountry = defaultAddress?.country ?? "AU";
  if (defaultCountry && defaultCountry.length === 2) {
    const c = countryFromCode(defaultCountry);
    defaultCountry = c?.name ?? "Australia";
  }

  // Get the current organization type configuration
  const currentOrgType = organizationTypes.find(
    (type) => type.code === selectedType
  );

  // Create dynamic schema based on selected type
  const organisationFormSchema = createDynamicOrganisationSchema(
    selectedType,
    organizationTypes
  );
  type TOrganisationSchema = z.infer<typeof organisationFormSchema>;

  // Mutation queries
  const createOrganisationMutation = useCreateOrganisation();

  const updateOrganisationMutation = useUpdateOrganisation();

  // Form init

  const defaultValues = {
    type: (selectedOrganisation?.type as OrganisationType) || ("" as const),
    name: selectedOrganisation?.name || "",
    abn: selectedOrganisation?.abn || "",
    uuid: selectedOrganisation?.uuid || "",
    phoneNumber: {
      number: defaultPhoneNumber?.number ?? "",
      type: defaultPhoneNumber?.type ?? PhoneNumberType.DEFAULT,
      uuid: defaultPhoneNumber?.uuid,
    },
    address: {
      country: defaultCountry,
      line1: defaultAddress?.line1 ?? "",
      line2: defaultAddress?.line2 ?? "",
      line3: defaultAddress?.line3 ?? "",
      line4: defaultAddress?.line4 ?? "",
      city: defaultAddress?.city ?? "",
      state: defaultAddress?.state ?? "",
      postcode: defaultAddress?.postcode ?? "",
      type: defaultAddress?.type ?? AddressType.HOME,
      uuid: defaultAddress?.uuid,
    },
  };
  const form = useForm<TOrganisationSchema>({
    resolver: zodResolver(organisationFormSchema),
    defaultValues,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  // Watch for type changes to update validation
  const watchedType = form.watch("type");

  useEffect(() => {
    if (watchedType && watchedType !== selectedType) {
      setSelectedType(watchedType);
      form.clearErrors();
    }
  }, [watchedType, selectedType, form, organizationTypes]);

  const onSelectOrganisation = useCallback(
    (organisation: Organisation | null) => {
      if (organisation) {
        setSelectedOrganisation(organisation);
        findDefaultOrganisationPhoneNumber;
        form.setValue("type", organisation.type!);
        form.setValue("name", organisation.name!);
        form.setValue("abn", organisation.abn!);
        const defaultPhoneNumber =
          findDefaultOrganisationPhoneNumber(organisation);
        const defaultAddress = findDefaultOrganisationAddress(organisation);
        form.setValue("phoneNumber.number", defaultPhoneNumber?.number ?? "");
        form.setValue(
          "phoneNumber.type",
          defaultPhoneNumber?.type ?? PhoneNumberType.DEFAULT
        );
        form.setValue("phoneNumber.uuid", defaultPhoneNumber?.uuid);
        form.setValue("address.country", defaultAddress?.country ?? "");
        form.setValue("address.line1", defaultAddress?.line1 ?? "");
        form.setValue("address.line2", defaultAddress?.line2 ?? "");
        form.setValue("address.line3", defaultAddress?.line3 ?? "");
        form.setValue("address.line4", defaultAddress?.line4 ?? "");
        form.setValue("address.city", defaultAddress?.city ?? "");
        form.setValue("address.state", defaultAddress?.state ?? "");
        form.setValue("address.postcode", defaultAddress?.postcode ?? "");
        form.setValue("address.type", defaultAddress?.type ?? AddressType.HOME);
        form.setValue("address.uuid", defaultAddress?.uuid);

        form.setValue("uuid", organisation.uuid!);
      } else {
        form.reset(defaultValues);
      }
    },
    [form]
  );

  const saveOrganisation = useCallback(
    async (data: TOrganisationSchema, syncOwner: boolean) => {
      setIsLoading(true);

      const parsedPhoneNumber = data.phoneNumber?.number
        ? phoneNumbersSchema.safeParse(data.phoneNumber)
        : null;
      const parsedAddress = addressSchema.safeParse(data.address);
      const phoneNumbers = parsedPhoneNumber?.success
        ? [parsedPhoneNumber.data]
        : undefined;
      const addresses = parsedAddress.success ? [parsedAddress.data] : undefined;

      const orgData = {
        type: data.type === "" ? undefined : data.type,
        name: data.name?.trim(),
        abn: data.abn?.trim(),
        ...(phoneNumbers ? { phoneNumbers } : {}),
        ...(addresses ? { addresses } : {}),
      };

      try {
        const savedOrganisation = selectedOrganisation
          ? await updateOrganisationMutation.mutateAsync({
            ...orgData,
            uuid: selectedOrganisation.uuid!,
          })
          : await createOrganisationMutation.mutateAsync(orgData);

        onSelectOrganisation(savedOrganisation);

        if (syncOwner) {
          await onSuccess(savedOrganisation.uuid!);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [
      createOrganisationMutation,
      onSelectOrganisation,
      onSuccess,
      selectedOrganisation,
      updateOrganisationMutation,
    ]
  );

  const onSubmit = async (data: TOrganisationSchema) => {
    await saveOrganisation(data, true);
  };

  useEffect(() => {
    if (order?.owner) {
      if (order.owner.type === OwnerType.ORGANISATION) {
        const organisation = user?.organisations?.find(
          (organisation) =>
            organisation.uuid === order.owner?.organisation?.uuid
        );
        onSelectOrganisation(organisation || null);
      } else if (order.owner.type !== OwnerType.USER) {
        navigate("signin");
      }
    }
  }, [order?.owner, onSelectOrganisation, user]);

  if (isLoadingUser || isLoadingOrder || isLoadingOrgTypes) {
    return <LoadingSection />;
  }

  return (
    <>
      <FormCard
        form={form}
        onSubmit={onSubmit}
        title="Company Information"
        description=""
      >
        <OrganisationFormEffects
          form={form}
          saveOrganisation={saveOrganisation}
          isValidSilently={(values) => organisationFormSchema.safeParse(values).success}
        />
        {user?.organisations && user?.organisations.length > 0 && (
          <>
            <ExistingOrganisations
              organisations={user?.organisations}
              setOrganisation={onSelectOrganisation}
              selectedOrganisation={selectedOrganisation}
            />
            {!isAddingNewOrganisation && !selectedOrganisation && (
              <div className="flex flex-col gap-2">
                <div className="border-b w-full border-brand-neutral-700" />
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsAddingNewOrganisation(true);
                  }}
                  className="text-muted-foreground hover:bg-transparent border border-transparent hover:border-brand-neutral-700 rounded-sm"
                >
                  Add new Organisation
                  <PlusIcon className="w-4 h-4" />
                </Button>
                <div className="border-b w-full border-brand-neutral-700" />
              </div>
            )}
            {selectedOrganisation && !isAddingNewOrganisation && (
              <div className="flex flex-col gap-2">
                <div className="border-b w-full border-brand-neutral-700" />
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsAddingNewOrganisation(true);
                  }}
                  className="text-muted-foreground hover:bg-transparent border border-transparent hover:border-brand-neutral-700 rounded-sm"
                >
                  Edit Organisation
                  <EditIcon className="w-4 h-4" />
                </Button>
                <div className="border-b w-full border-brand-neutral-700" />
              </div>
            )}
          </>
        )}
        {(user?.organisations?.length === 0 ||
          !user?.organisations ||
          isAddingNewOrganisation) && (
            <>
              {(selectedOrganisation || isAddingNewOrganisation) && (
                <div className="border-b w-full border-brand-neutral-700" />
              )}
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value ?? ""}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select your company type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {organizationTypes
                          .filter((orgType) => orgType.code !== "Individual")
                          .map((orgType) => (
                            <SelectItem key={orgType.code} value={orgType.code!}>
                              {orgType.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {currentOrgType?.flags.abnRequired && (
                <FormField
                  control={form.control}
                  name="abn"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ABN</FormLabel>
                      <FormControl>
                        <ABNInput {...field} value={field.value || ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {currentOrgType?.flags.nameRequired && (
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="phoneNumber.number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Phone number (optional)</FormLabel>
                    <FormControl>
                      <CustomPhoneInput
                        value={field.value ?? ""}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="border-b w-full border-brand-neutral-700" />
              <AddressForm
                form={form}
                name="address"
                placeholder="Search for your address"
              />
            </>
          )}
      </FormCard>
    </>
  );
}

function OrganisationFormEffects<TFormValues extends FieldValues>({
  form,
  saveOrganisation,
  isValidSilently,
}: {
  form: UseFormReturn<TFormValues>;
  saveOrganisation: (data: TFormValues, syncOwner: boolean) => Promise<void>;
  isValidSilently: (values: TFormValues) => boolean;
}) {
  const setSecondaryHandler = usePaymentGatekeeperStore(
    (state) => state.setSecondaryHandler
  );
  const gatekeeperHandler = useCallback(async (options?: PaymentGatekeeperOptions) => {
    if (options?.silent) {
      return isValidSilently(form.getValues());
    }

    const isValid = await form.trigger(undefined, { shouldFocus: false });

    if (!isValid) {
      scrollToFirstInvalidField(form);
      return false;
    }

    await saveOrganisation(form.getValues(), true);
    form.reset(form.getValues(), { keepValues: true, keepTouched: true });
    return true;
  }, [form, isValidSilently, saveOrganisation]);

  useEffect(() => {
    setSecondaryHandler(gatekeeperHandler);

    return () => {
      setSecondaryHandler(null);
    };
  }, [gatekeeperHandler, setSecondaryHandler]);

  return null;
}
